import * as React from "react";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import {
    fetchTrackingThreshold,
    saveTrackingThreshold,
    resetTrackingThresholdApi,
} from "./trackingThreshold.service";
import type { TrackingSource, TrackingDelays } from "./trackingThreshold.types";
import "./TrackingThresholdDrawer.css";

interface Props {
    open: boolean;
    organizationId: string;
    onClose: () => void;
}

const FIELDS: { source: TrackingSource; label: string; hint: string }[] = [
    { source: "awb-subscribe", label: "Air (API)", hint: "Air shipments added via API" },
    { source: "awb-track-hub", label: "Air (Frontend)", hint: "Air shipments added via Frontend" },
    { source: "ocean-subscribe", label: "Ocean (API)", hint: "Ocean shipments added via API" },
    { source: "ocean-track-hub", label: "Ocean (Frontend)", hint: "Ocean shipments added via Frontend" },
];

const MAX_MIN = 525600;
const MAX_HRS = 8760;

// Each field now holds an {hours, minutes} pair as strings.
type HM = { h: string; m: string };
type ValueMap = Record<TrackingSource, HM>;
type ErrorMap = Record<TrackingSource, string>;

const emptyValues = (): ValueMap => ({
    "awb-subscribe": { h: "", m: "" },
    "awb-track-hub": { h: "", m: "" },
    "ocean-subscribe": { h: "", m: "" },
    "ocean-track-hub": { h: "", m: "" },
});

const emptyErrors = (): ErrorMap => ({
    "awb-subscribe": "",
    "awb-track-hub": "",
    "ocean-subscribe": "",
    "ocean-track-hub": "",
});

// Block decimal point and other non-integer keys at keystroke.
const blockNonInteger = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
};

// minutes (from API) -> {h, m} for the two inputs. null/0 -> both blank (immediate).
const minutesToHM = (total: number | null | undefined): HM => {
    if (total == null || total <= 0) return { h: "", m: "" };
    const h = Math.floor(total / 60);
    const m = total - h * 60; // preserves fractional minutes if any
    return { h: h ? String(h) : "", m: m ? String(m) : "" };
};

// {h, m} -> total minutes | null (blank both = null = immediate). Returns error string on invalid.
const hmToMinutes = (hm: HM): { minutes: number | null; error: string } => {
    const hRaw = hm.h.trim();
    const mRaw = hm.m.trim();

    if (hRaw === "" && mRaw === "") return { minutes: null, error: "" };

    const h = hRaw === "" ? 0 : Number(hRaw);
    const m = mRaw === "" ? 0 : Number(mRaw);

    if (!Number.isInteger(h) || !Number.isInteger(m)) return { minutes: null, error: "Use whole numbers" };
    if (h < 0 || m < 0) return { minutes: null, error: "Cannot be negative" };
    if (m > 59) return { minutes: null, error: "Minutes must be 0–59" };

    const total = h * 60 + m;
    if (total <= 0) return { minutes: null, error: "" };
    if (total > MAX_MIN) return { minutes: null, error: "Max 365 days" };

    return { minutes: total, error: "" };
};

// Human summary for the help line, e.g. "6h 30m" / "45m" / "2h".
const summarize = (minutes: number | null): string => {
    if (minutes == null || minutes <= 0) return "";
    const h = Math.floor(minutes / 60);
    const m = minutes - h * 60;
    const parts: string[] = [];
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    return parts.join(" ");
};

export default function TrackingThresholdDrawer({ open, organizationId, onClose }: Props) {
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [values, setValues] = React.useState<ValueMap>(emptyValues());
    const [errors, setErrors] = React.useState<ErrorMap>(emptyErrors());

    React.useEffect(() => {
        if (!open || !organizationId) return;
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const res = await fetchTrackingThreshold(organizationId);
                const delays: TrackingDelays = res?.data?.data?.delays || ({} as TrackingDelays);
                if (!alive) return;
                setValues({
                    "awb-subscribe": minutesToHM(delays["awb-subscribe"]),
                    "awb-track-hub": minutesToHM(delays["awb-track-hub"]),
                    "ocean-subscribe": minutesToHM(delays["ocean-subscribe"]),
                    "ocean-track-hub": minutesToHM(delays["ocean-track-hub"]),
                });
                setErrors(emptyErrors());
            } catch {
                if (alive) toast.error("Failed to load tracking threshold");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [open, organizationId]);


    // Lock background scroll + interaction while open (compensate scrollbar to avoid layout shift).
    React.useEffect(() => {
        if (!open) return;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const prevOverflow = document.body.style.overflow;
        const prevPaddingRight = document.body.style.paddingRight;
        document.body.style.overflow = "hidden";
        if (scrollbarWidth > 0) {
            const current = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
            document.body.style.paddingRight = `${current + scrollbarWidth}px`;
        }
        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.paddingRight = prevPaddingRight;
        };
    }, [open]);



    const handleChange = (source: TrackingSource, part: "h" | "m", raw: string) => {
        setValues((p) => {
            let val = raw;
            if (raw !== "") {
                const n = Number(raw);
                if (Number.isFinite(n)) {
                    // Minutes are 0–59; hours cap at 8760 (365 days).
                    if (part === "m" && n > 59) val = "59";
                    if (part === "h" && n > MAX_HRS) val = String(MAX_HRS);
                }
            }
            const nextHM = { ...p[source], [part]: val };
            setErrors((e) => ({ ...e, [source]: hmToMinutes(nextHM).error }));
            return { ...p, [source]: nextHM };
        });
    };

    const buildDelays = (): TrackingDelays | null => {
        const next = {} as TrackingDelays;
        const nextErrors = emptyErrors();
        let hasError = false;
        for (const { source } of FIELDS) {
            const { minutes, error } = hmToMinutes(values[source]);
            nextErrors[source] = error;
            if (error) hasError = true;
            next[source] = minutes;
        }
        setErrors(nextErrors);
        return hasError ? null : next;
    };

    const handleSave = async () => {
        const delays = buildDelays();
        if (!delays) {
            toast.error("Fix the highlighted fields");
            return;
        }
        setSaving(true);
        try {
            await saveTrackingThreshold(organizationId, delays);
            toast.success("Tracking threshold saved");
            onClose();
        } catch (err: any) {
            toast.error(err?.response?.data?.error || "Failed to save tracking threshold");
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        setSaving(true);
        try {
            await resetTrackingThresholdApi(organizationId);
            setValues(emptyValues());
            setErrors(emptyErrors());
            toast.success("Reset to immediate tracking");
        } catch (err: any) {
            toast.error(err?.response?.data?.error || "Failed to reset");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <>
            <div className="ttd-overlay" onClick={onClose} />
            <aside className="ttd-drawer" role="dialog" aria-label="Tracking Threshold">
                <header className="ttd-header">
                    <div>
                        <h3 className="ttd-title">Tracking Threshold</h3>
                        <p className="ttd-subtitle">Delay before tracking starts</p>
                    </div>
                    <button className="ttd-close" onClick={onClose} aria-label="Close">
                        <CloseIcon fontSize="small" />
                    </button>
                </header>

                <div className="ttd-body">
                    {loading ? (
                        <div className="ttd-loading">Loading…</div>
                    ) : (
                        FIELDS.map(({ source, label, hint }) => {
                            const hm = values[source];
                            const err = errors[source];
                            const { minutes } = hmToMinutes(hm);
                            const isImmediate = hm.h.trim() === "" && hm.m.trim() === "";
                            return (
                                <div className="ttd-field" key={source}>
                                    <label className="ttd-label">{label}</label>
                                    <div className="ttd-hm">
                                        <div className="ttd-hm-unit">
                                            <input
                                                className={`ttd-input${err ? " ttd-input-error" : ""}`}
                                                type="number"
                                                min={0}
                                                max={8760}
                                                step={1}
                                                placeholder="0"
                                                value={hm.h}
                                                onKeyDown={blockNonInteger}
                                                onChange={(e) => handleChange(source, "h", e.target.value)}
                                            />
                                            <span className="ttd-hm-suffix">hrs</span>
                                        </div>
                                        <div className="ttd-hm-unit">
                                            <input
                                                className={`ttd-input${err ? " ttd-input-error" : ""}`}
                                                type="number"
                                                min={0}
                                                max={59}
                                                step={1}
                                                placeholder="0"
                                                value={hm.m}
                                                onKeyDown={blockNonInteger}
                                                onChange={(e) => handleChange(source, "m", e.target.value)}
                                            />
                                            <span className="ttd-hm-suffix">min</span>
                                        </div>
                                    </div>
                                    {err ? (
                                        <span className="ttd-error">{err}</span>
                                    ) : (
                                        <span className="ttd-help">
                                            {isImmediate
                                                ? `${hint} — tracking starts immediately`
                                                : `Tracking starts ${summarize(minutes)} after the shipment is added`}
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <footer className="ttd-footer">
                    <button className="ttd-btn ttd-btn-ghost" onClick={handleReset} disabled={saving || loading}>
                        Reset all
                    </button>
                    <div className="ttd-footer-right">
                        <button className="ttd-btn ttd-btn-cancel" onClick={onClose} disabled={saving}>
                            Cancel
                        </button>
                        <button
                            className="ttd-btn btn btnYellow ttd-btn-save"
                            onClick={handleSave}
                            disabled={saving || loading}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </footer>
            </aside>
        </>
    );
}